import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { Alert, Button, Drawer, Table, Tag } from 'antd'

import { HIGH_VALUE_BASKET_ONLINE_PAYMENTS_QUERY } from './graphql'
import { formatBaht } from './format'

const PAGE_SIZE = 50

const namesOf = staffs => (staffs || []).map(staff => staff.fullName).join(', ') || '-'

const OnlinePaymentsDrawer = ({ month, onClose, open }) => {
  const { i18n } = useTranslation('highValueBaskets')
  const t = (key, options) => i18n.t(`highValueBaskets:${key}`, options)

  const { loading, data, error, fetchMore } = useQuery(HIGH_VALUE_BASKET_ONLINE_PAYMENTS_QUERY, {
    variables: { month, first: PAGE_SIZE },
    skip: !open,
    fetchPolicy: 'cache-and-network'
  })

  const connection = (data && data.highValueBasketOnlinePayments) || null
  const nodes = (connection && connection.nodes) || []

  const onLoadMore = () => {
    fetchMore({
      variables: { after: connection.pageInfo.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous

        return {
          highValueBasketOnlinePayments: {
            ...fetchMoreResult.highValueBasketOnlinePayments,
            nodes: [
              ...previous.highValueBasketOnlinePayments.nodes,
              ...fetchMoreResult.highValueBasketOnlinePayments.nodes
            ]
          }
        }
      }
    })
  }

  const columns = [
    {
      key: 'number',
      title: t('online.columns.number'),
      render: payment => <Link to={`/inquiries/${payment.orderNumber}`}>{payment.orderNumber}</Link>
    },
    { key: 'transferDate', title: t('online.columns.transferDate'), dataIndex: 'transferDate' },
    {
      key: 'amount',
      align: 'right',
      title: t('online.columns.amount'),
      render: payment => formatBaht(payment.amount)
    },
    {
      key: 'receipt',
      title: t('online.columns.receipt'),
      render: payment => payment.receiptVoucher || <Tag color="orange">{t('online.awaitingReceipt')}</Tag>
    },
    { key: 'sales', title: t('online.columns.sales'), render: payment => namesOf(payment.sales) },
    { key: 'cses', title: t('online.columns.cses'), render: payment => namesOf(payment.cses) }
  ]

  return (
    <Drawer
      onClose={onClose}
      open={open}
      title={t('online.title', { month: month.slice(0, 7) })}
      width={Math.min(960, typeof window === 'undefined' ? 960 : window.innerWidth)}
    >
      {error && <Alert message={error.message} showIcon style={{ marginBottom: 16 }} type="error" />}
      <Table
        columns={columns}
        dataSource={nodes}
        loading={loading}
        locale={{ emptyText: t('online.empty') }}
        pagination={false}
        rowKey="id"
        scroll={{ x: true }}
        size="small"
      />
      {connection && connection.pageInfo.hasNextPage && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button loading={loading} onClick={onLoadMore}>
            {t('leads.loadMore', { shown: nodes.length })}
          </Button>
        </div>
      )}
    </Drawer>
  )
}

OnlinePaymentsDrawer.propTypes = {
  month: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
}

export default OnlinePaymentsDrawer
